import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, MapPin, Bed, Bath, Car, Maximize, ArrowRight } from 'lucide-react';
import { formatCOP } from '../../mock';
import { thumbUrl } from '../../lib/api';

/**
 * ProvisionalRecommendationCard — la propiedad que el Advisor considera hoy
 * la mejor opción para el usuario.
 *
 * Reglas de implementación aplicadas:
 *  · R2 · Se ve especial, no primera — layout distinto, jerarquía visual clara.
 *  · R3 · La explicación cita evidencia concreta (usa `explanation.human_readable`
 *         que ya viene del ExplainabilityEngine).
 *  · P4 · Si viene un `evolutionMessage`, lo muestra en un strip superior
 *         framing evolución, no error.
 *
 * Props:
 *   - item: RecommendationItem del RecommendationEngine con { snapshot, explanation, ... }
 *   - firmnessLabel: string (ej. 'Sugerencia inicial', 'Recomendación provisional')
 *   - confidencePct: número 0-100
 *   - evolutionMessage: string opcional — mensaje de "evolucionó porque..."
 */
export default function ProvisionalRecommendationCard({
  item,
  firmnessLabel,
  confidencePct,
  evolutionMessage,
}) {
  if (!item || !item.snapshot) return null;
  const snap = item.snapshot;
  const cover =
    snap.cover_image
      ? thumbUrl(snap.cover_image, { w: 960, h: 720 })
      : 'https://via.placeholder.com/960x720?text=Sin+foto';
  const explanation = item.explanation?.human_readable || '';
  const href = `/propiedad/${item.item_id || snap.id}`;

  return (
    <article
      className="mb-6 rounded-3xl overflow-hidden border-2 border-habita/40 bg-white shadow-md"
      data-testid="provisional-recommendation-card"
    >
      {/* Strip de evolución — sólo si aplica (R4 · framing de aprendizaje) */}
      {evolutionMessage && (
        <div
          className="px-5 py-2.5 bg-habita-mint/60 border-b border-habita/20 text-sm text-habita-dark"
          data-testid="recommendation-evolution-strip"
        >
          <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-habita" />
          {evolutionMessage}
        </div>
      )}

      <Link to={href} className="grid md:grid-cols-[380px_1fr] group">
        <div className="relative aspect-[4/3] md:aspect-auto bg-gray-100 overflow-hidden">
          <img
            src={cover}
            alt={snap.title || 'Recomendación provisional'}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            loading="lazy"
          />
        </div>

        <div className="p-6 sm:p-7 flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-habita-mint text-habita-dark text-[10px] uppercase tracking-widest font-bold"
              data-testid="firmness-badge"
            >
              <Sparkles className="w-3 h-3" />
              {firmnessLabel}
            </span>
            {typeof confidencePct === 'number' && (
              <span className="text-[11px] text-gray-500">
                Confianza {Math.round(confidencePct)}% · sube con cada acción
              </span>
            )}
          </div>

          <h2 className="font-serif text-2xl font-bold text-habita-dark leading-tight mb-1 line-clamp-2">
            {snap.title || 'Propiedad'}
          </h2>
          <p className="text-sm text-gray-500 flex items-center gap-1 mb-4">
            <MapPin className="w-3.5 h-3.5" /> {snap.city || ''}
          </p>

          {/* Bloque de explicación — cita evidencia concreta (R3) */}
          {explanation && (
            <blockquote
              className="text-sm text-habita-dark bg-habita-mint/40 border-l-2 border-habita pl-3 py-2 mb-4 italic"
              data-testid="recommendation-explanation"
            >
              {explanation}
            </blockquote>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700 mb-5">
            {typeof snap.beds === 'number' && (
              <span className="inline-flex items-center gap-1">
                <Bed className="w-4 h-4" /> {snap.beds}
              </span>
            )}
            {typeof snap.baths === 'number' && (
              <span className="inline-flex items-center gap-1">
                <Bath className="w-4 h-4" /> {snap.baths}
              </span>
            )}
            {typeof snap.parking === 'number' && (
              <span className="inline-flex items-center gap-1">
                <Car className="w-4 h-4" /> {snap.parking}
              </span>
            )}
            {typeof snap.area === 'number' && (
              <span className="inline-flex items-center gap-1">
                <Maximize className="w-4 h-4" /> {snap.area} m²
              </span>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 mt-auto pt-3 border-t border-gray-100">
            <p className="text-2xl font-bold text-habita">{formatCOP(snap.price)}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-habita group-hover:gap-2 transition-all">
              Ver por qué la elegí <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}
